/**
 * Transaction Filters Component
 * 
 * Inline horizontal filter bar for searching transactions.
 */

import * as React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Search, RotateCcw } from "lucide-react";
import { format } from "date-fns";
import { id as idLocale } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { LocationSelect } from "@/components/ui/LocationSelect";
import type { useTransactionFilters } from "../hooks/useTransactionFilters";

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

interface TransactionFiltersProps {
  filters: ReturnType<typeof useTransactionFilters>;
  loading: boolean;
  onApply: () => void;
  onClear: () => void;
  allowedProvinces?: string[];
  provinceLocked?: boolean;
  allowedCities?: string[];
  cityLocked?: boolean;
}

export function TransactionFilters({
  filters,
  loading,
  onApply,
  onClear,
  allowedProvinces,
  provinceLocked = false,
  allowedCities,
  cityLocked = false,
}: TransactionFiltersProps) {
  const hasActiveFilters = filters.fromDate || filters.toDate || filters.orderIdSearch || filters.province || filters.city;

  // Debounced Order ID search
  const [localOrderId, setLocalOrderId] = React.useState(filters.orderIdSearch);
  const debouncedOrderId = useDebounce(localOrderId, 400);

  React.useEffect(() => {
    setLocalOrderId(filters.orderIdSearch);
  }, [filters.orderIdSearch]);

  React.useEffect(() => {
    if (debouncedOrderId !== filters.orderIdSearch) {
      filters.setOrderIdSearch(debouncedOrderId);
      filters.orderIdSearchRef.current = debouncedOrderId;
      const currentFromDate = filters.fromDateRef.current;
      const currentToDate = filters.toDateRef.current;
      const currentProvince = filters.provinceRef.current;
      const currentCity = filters.cityRef.current;
      filters.updateFiltersInUrl(currentFromDate, currentToDate, debouncedOrderId, currentProvince, currentCity);
    }
  }, [debouncedOrderId, filters]);

  return (
    <div className="space-y-3">
      {/* Search + Date Row */}
      <div className="flex flex-wrap items-end gap-2">
        {/* From Date */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-[140px] h-9 justify-start text-left font-normal text-xs",
                !filters.fromDate && "text-muted-foreground"
              )}
              disabled={loading}
            >
              <CalendarIcon className="mr-1.5 h-3.5 w-3.5" />
              {filters.fromDate ? format(filters.fromDate, "dd/MM/yy", { locale: idLocale }) : "Dari"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={filters.fromDate}
              onSelect={(date) => {
                filters.setFromDate(date);
                const currentToDate = filters.toDateRef.current;
                const currentOrderId = filters.orderIdSearchRef.current;
                const currentProvince = filters.provinceRef.current;
                const currentCity = filters.cityRef.current;
                filters.updateFiltersInUrl(date, currentToDate, currentOrderId, currentProvince, currentCity);
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        {/* To Date */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-[140px] h-9 justify-start text-left font-normal text-xs",
                !filters.toDate && "text-muted-foreground"
              )}
              disabled={loading}
            >
              <CalendarIcon className="mr-1.5 h-3.5 w-3.5" />
              {filters.toDate ? format(filters.toDate, "dd/MM/yy", { locale: idLocale }) : "Sampai"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={filters.toDate}
              onSelect={(date) => {
                filters.setToDate(date);
                const currentFromDate = filters.fromDateRef.current;
                const currentOrderId = filters.orderIdSearchRef.current;
                const currentProvince = filters.provinceRef.current;
                const currentCity = filters.cityRef.current;
                filters.updateFiltersInUrl(currentFromDate, date, currentOrderId, currentProvince, currentCity);
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        {/* Order ID Search */}
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Cari Order ID..."
            value={localOrderId}
            onChange={(e) => setLocalOrderId(e.target.value)}
            className="pl-8 h-9 text-xs"
            onKeyDown={(e) => { if (e.key === "Enter") onApply(); }}
            disabled={loading}
          />
        </div>

        <Button onClick={onApply} disabled={loading} size="sm" className="h-9 px-4">
          Terapkan
        </Button>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={onClear} disabled={loading} className="h-9 px-2 text-muted-foreground hover:text-foreground">
            <RotateCcw className="h-3.5 w-3.5 mr-1" />
            Reset
          </Button>
        )}
      </div>

      {/* Location Row */}
      <div className="flex flex-wrap items-end gap-2">
        <LocationSelect
          province={filters.province}
          city={filters.city}
          onProvinceChange={(province) => {
            filters.setProvince(province);
            filters.setCity(undefined);
            filters.provinceRef.current = province;
            filters.cityRef.current = undefined;
            const currentFromDate = filters.fromDateRef.current;
            const currentToDate = filters.toDateRef.current;
            const currentOrderId = filters.orderIdSearchRef.current;
            filters.updateFiltersInUrl(currentFromDate, currentToDate, currentOrderId, province, undefined);
            onApply();
          }}
          onCityChange={(city) => {
            filters.setCity(city);
            filters.cityRef.current = city;
            const currentFromDate = filters.fromDateRef.current;
            const currentToDate = filters.toDateRef.current;
            const currentOrderId = filters.orderIdSearchRef.current;
            const currentProvince = filters.provinceRef.current;
            filters.updateFiltersInUrl(currentFromDate, currentToDate, currentOrderId, currentProvince, city);
            onApply();
          }}
          disabled={loading}
          showLabels={false}
          allowedProvinces={allowedProvinces}
          provinceLocked={provinceLocked}
          allowedCities={allowedCities}
          cityLocked={cityLocked}
        />
      </div>
    </div>
  );
}
